import { useEffect, useState } from "react";
import type { TeacherResponse, UserInfo } from "p6m-p6u";

export type AnimationPhase = "initial" | "showing-onboarding" | "showing-typewriter" | "show-check" | "idle";

interface UseOnboardingAnimationParams {
    teacherInfo: TeacherResponse | null;
    userInfo: UserInfo;
    isOnboardingComplete: boolean;
    isActivityDataLoaded: boolean;
    isVocabTrainingStatusKnown: boolean;
    hasActivityLoadError: boolean;
}

export const useOnboardingAnimation = (params: UseOnboardingAnimationParams) => {
    const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("initial");
    const [typewriterText, setTypewriterText] = useState("");

    const ONBOARDING_IN_PROGRESS_KEY = "ONBOARDING_IN_PROGRESS";

    useEffect(() => {
        if (!params.teacherInfo || !params.userInfo || !params.isActivityDataLoaded) return;

        // Skip onboarding when activity data is missing or errored — otherwise a
        // failed load could falsely mark a step incomplete and trigger celebration.
        // Only snap to idle from "initial" so a transient mid-flow error doesn't
        // interrupt a running animation.
        if (params.hasActivityLoadError || !params.isVocabTrainingStatusKnown) {
            setAnimationPhase((prev) => (prev === "initial" ? "idle" : prev));
            return;
        }

        const isOnboardingInProgress = localStorage.getItem(ONBOARDING_IN_PROGRESS_KEY) === "true";

        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (!params.isOnboardingComplete) {
            localStorage.setItem(ONBOARDING_IN_PROGRESS_KEY, "true");

            timeoutId = setTimeout(() => {
                setAnimationPhase("showing-onboarding");
            }, 500);
        } else if (params.isOnboardingComplete && isOnboardingInProgress) {
            // Onboarding just completed - show success animation
            // Ensure onboarding is expanded to show completion
            localStorage.setItem("IS_ONBOARDING_EXPANDED", "true");

            timeoutId = setTimeout(() => {
                setAnimationPhase("showing-onboarding");
            }, 500);
        } else {
            // Onboarding complete and no marker (already celebrated or completed on another device)
            setAnimationPhase("idle");
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [
        params.teacherInfo,
        params.userInfo,
        params.isOnboardingComplete,
        params.isActivityDataLoaded,
        params.isVocabTrainingStatusKnown,
        params.hasActivityLoadError,
    ]);

    useEffect(() => {
        if (animationPhase === "showing-onboarding" && params.isOnboardingComplete) {
            // Progress to celebration animation after showing completed onboarding
            // Timing: 300ms (collapse animation for main content) + 3000ms (visible time) = 3300ms
            const timeoutId = setTimeout(() => {
                setAnimationPhase("showing-typewriter");
            }, 3300);

            return () => clearTimeout(timeoutId);
        }

        if (animationPhase === "showing-typewriter") {
            const fullText = "100%";
            let currentIndex = 0;

            const intervalId = setInterval(() => {
                if (currentIndex <= fullText.length) {
                    setTypewriterText(fullText.slice(0, currentIndex));
                    currentIndex++;
                } else {
                    clearInterval(intervalId);
                    // Show 'check icon' after typewriter finishes
                    setAnimationPhase("show-check");
                    // Once typewriter is done, wait a bit then collapse
                    setTimeout(() => {
                        setAnimationPhase("idle");
                        localStorage.removeItem(ONBOARDING_IN_PROGRESS_KEY);
                    }, 1500);
                }
            }, 150);

            return () => clearInterval(intervalId);
        }
    }, [animationPhase, params.isOnboardingComplete]);

    return { animationPhase, typewriterText };
};
